/**
 * Barra de edicion masiva de la tabla de Productos (ADM-03).
 *
 * Aparece cuando hay filas seleccionadas y ofrece lo unico que el sitio
 * administra sobre varios productos a la vez: publicarlos u ocultarlos.
 * Precio y stock son de Random, y desde el 22-09-2026 la categoria tambien:
 * la sincronizacion la trae de la superfamilia y la familia del ERP en cada
 * corrida, asi que el "Cambiar categoria" que habia aca se saco — un cambio
 * hecho desde el panel duraba hasta la siguiente hora. El cliente ordena su
 * catalogo en Random, que es donde ya lo hace.
 *
 * Las dos operaciones piden confirmacion en un modal antes de aplicarse: a
 * diferencia de destacar un producto (un interruptor, reversible con otro
 * clic), esto puede tocar decenas de productos de una sola vez.
 */
import { useState } from 'react';

import { actualizarProductosMasivo } from '../../servicios/administracion.servicio';
import { numero } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { Icono } from '../comunes/Icono';
import { Modal } from './Modal';

export interface BarraEdicionMasivaProps {
  seleccionados: number[];
  onLimpiar(): void;
  /** Se dispara despues de aplicar con exito, para refrescar la tabla. */
  onAplicado(): void;
}

type Accion = 'publicar' | 'ocultar' | null;

function plural(cantidad: number, singular: string, plural: string): string {
  return cantidad === 1 ? singular : plural;
}

export function BarraEdicionMasiva({ seleccionados, onLimpiar, onAplicado }: BarraEdicionMasivaProps) {
  const [accion, setAccion] = useState<Accion>(null);
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);

  if (seleccionados.length === 0) return null;

  const cantidad = seleccionados.length;

  function cerrar(): void {
    if (enviando) return;
    setAccion(null);
    setFalla(null);
  }

  async function aplicar(visible: boolean): Promise<void> {
    setEnviando(true);
    setFalla(null);
    try {
      await actualizarProductosMasivo({ ids: seleccionados, visible });
      cerrar();
      onAplicado();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  return (
    <>
      <div className="barra-edicion-masiva" role="region" aria-label="Edición masiva de productos">
        <span className="n">
          {numero(cantidad)} {plural(cantidad, 'producto seleccionado', 'productos seleccionados')}
        </span>
        <span className="acciones-fila">
          <button
            type="button"
            className="btn btn-sm btn-contorno"
            onClick={() => {
              setAccion('publicar');
            }}
          >
            <Icono nombre="ojo" />
            Publicar
          </button>
          <button
            type="button"
            className="btn btn-sm btn-contorno"
            onClick={() => {
              setAccion('ocultar');
            }}
          >
            <Icono nombre="ojoNo" />
            Ocultar
          </button>
          <button type="button" className="btn btn-sm btn-suave" onClick={onLimpiar}>
            Cancelar selección
          </button>
        </span>
      </div>

      <Modal
        titulo={accion === 'publicar' ? 'Publicar productos' : 'Ocultar productos'}
        bajada={`${numero(cantidad)} ${plural(cantidad, 'producto', 'productos')}`}
        abierto={accion !== null}
        onCerrar={cerrar}
      >
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        {accion !== null ? (
          <div className="formulario-admin">
            <p>
              {accion === 'publicar'
                ? `Vas a publicar ${numero(cantidad)} ${plural(cantidad, 'producto', 'productos')}. Va a aparecer donde el catálogo lo muestre.`
                : `Vas a ocultar ${numero(cantidad)} ${plural(cantidad, 'producto', 'productos')}. Deja de verse en la tienda hasta que lo vuelvas a publicar.`}
            </p>
            <div className="modal-acciones">
              <button type="button" className="btn btn-contorno" onClick={cerrar} disabled={enviando}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primario"
                disabled={enviando}
                onClick={() => {
                  void aplicar(accion === 'publicar');
                }}
              >
                {enviando ? 'Aplicando…' : accion === 'publicar' ? 'Publicar' : 'Ocultar'}
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
